import React, { useContext, useState } from 'react';
import {
	MAX_CART_ITEM_COUNT,
	MAX_CART_ITEM_SUM,
	ICartOverlapItem,
	ITypeVignetteOrderingProcessFields,
	IVignetteOffer,
	OfferContext,
	VignetteContext,
	formatDate,
	formatPlates,
	getLabelValue,
} from '@adac-mp/shared';
import { Modal, ProductSummary, ProductSummaryElement, Typography, renderProductIcon } from '../../index';
import { useFlowId } from '../../hooks/useFlowId';

interface IValidationModalProps {
	isEditing?: boolean;
	cartOverlapItems?: ICartOverlapItem[];
	errorCode: number;
	isOpen: boolean;
	onSubmit: () => void;
	onCancel?: () => void;
	onClose: () => void;
}

export const CartAndSystemValidationModal = ({
	errorCode,
	isOpen,
	isEditing = false,
	cartOverlapItems,
	onSubmit,
	onCancel,
	onClose,
}: IValidationModalProps) => {
	const flowId = useFlowId();
	const { contentfulLabels, selectedProducts } = useContext(VignetteContext);
	const selectedProduct = selectedProducts[0];
	const { labels } = useContext(OfferContext);
	const [openProductKey, setOpenProductKey] = useState<string | undefined>(undefined);
	const currentLabels = contentfulLabels as ITypeVignetteOrderingProcessFields;
	const vignetteModalLabels = currentLabels?.validationModal;
	const isOneCartFlow = flowId === 0;
	const isFlexService = selectedProduct?.moduleTypeId === 4;
	const cartOverlapping = errorCode === 60;
	const maxCartItemCountOrSum = errorCode === MAX_CART_ITEM_SUM || errorCode === MAX_CART_ITEM_COUNT;

	const handleToggle = (currentKey?: string) =>
		setOpenProductKey(openProductKey !== currentKey ? currentKey : undefined);

	const getValidationTitle = () => {
		if (maxCartItemCountOrSum) return vignetteModalLabels?.titleDuplicate || 'Achtung';
		if (isFlexService || isOneCartFlow) return vignetteModalLabels?.titleDuplicate || 'Achtung';
		return cartOverlapping
			? vignetteModalLabels?.titleDuplicate || 'Achtung'
			: vignetteModalLabels?.titleSystemDuplicate || 'Achtung! Bestehende Digitale Vignette';
	};

	const getValidationTextMessage = () => {
		const renderFlexCartOverlappingMessage = () =>
			vignetteModalLabels?.flexCartOverlappingLabel ||
			'Sie haben bereits einen FLEX-Service für das selbe Kennzeichen im Warenkorb. Bitte ändern Sie Ihre Eingabe.';
		const renderFlexSystemOverlappingMessage = () =>
			vignetteModalLabels?.flexSystemDuplicateLabel ||
			'Auf dieses Kennzeichen ist bereits ein FLEX-Service registriert. Bitte löschen Sie das Produkt oder ändern Sie Ihre Eingabe.';
		const renderVignetteCartOverlappingMessage = () =>
			vignetteModalLabels?.textDuplicate ||
			'Sie haben bereits eine Vignette mit selbem Kennzeichen und Startdatum in Ihrem Warenkorb. Bitte ändern Sie Ihre Eingabe.';
		const renderVignetteSystemOverlappingMessage = () =>
			vignetteModalLabels?.textSystemDuplicate ||
			'Es existiert bereits eine gültige digitale Vignette mit dem selbem Kennzeichen und Gültigkeitszeitraum. Bitte überprüfen Sie Ihre Eingaben.';
		const renderMultipleCartOverlappingMessage = () =>
			vignetteModalLabels?.multipleCartDuplicateText ||
			'Es befinden sich bereits mehrere Produkte mit selbem Kennzeichen und Startdatum in Ihrem Warenkorb. Bitte ändern Sie Ihre Eingabe.';
		const renderMultipleSystemOverlappingMessage = () =>
			vignetteModalLabels?.multipleSystemDuplicate ||
			'Eines oder mehrere ihrer konfigurierten Produkte existieren bereits im System. Bitte überprüfen Sie Ihre Eingabe.';
		const renderCartMaxItemOrMaxPriceMessage = () =>
			vignetteModalLabels?.maxItemCountOrPriceText ||
			'Die maximale Anzahl Produkte und/oder die Wertgrenze des Warenkorbes wurde erreicht. Ein hinzufügen weiterer Produkte ist über einen zweiten Warenkorb möglich.';

		if (maxCartItemCountOrSum) return renderCartMaxItemOrMaxPriceMessage();
		if (isOneCartFlow) {
			if (cartOverlapItems && cartOverlapItems.length === 1) {
				const isFlexProduct = cartOverlapItems[0].variationCode === 'FLEX';
				return isFlexProduct ? renderFlexCartOverlappingMessage() : renderVignetteCartOverlappingMessage();
			}
			if (cartOverlapItems && cartOverlapItems.length > 1) {
				return renderMultipleCartOverlappingMessage();
			}
			return renderMultipleSystemOverlappingMessage();
		}
		if (isFlexService) {
			return cartOverlapping ? renderFlexCartOverlappingMessage() : renderFlexSystemOverlappingMessage();
		}
		return cartOverlapping ? renderVignetteCartOverlappingMessage() : renderVignetteSystemOverlappingMessage();
	};

	const getSubmitButtonLabel = () => {
		if (maxCartItemCountOrSum) return vignetteModalLabels?.changeProductLabel || 'Eingabe ändern';
		if (isOneCartFlow) return vignetteModalLabels?.changeProductLabel || 'Eingabe ändern';
		if (isEditing && !isFlexService) return vignetteModalLabels?.labelContinue || 'Trotzdem fortfahren';
		return cartOverlapping || isFlexService
			? vignetteModalLabels?.changeProductLabel || 'Eingabe ändern'
			: vignetteModalLabels?.labelContinueSale || 'Kauf fortsetzen';
	};

	const getCancelButtonLabel = () => {
		if (isOneCartFlow) return vignetteModalLabels?.labelCancelSale || 'Entfernen und zum Warenkorb';
		if (isEditing) return getLabelValue('mp.cancelButton', labels) || 'Abbrechen';
		return cartOverlapping || isFlexService
			? vignetteModalLabels?.deleteProductLabel || 'Produkt löschen und zum Warenkorb'
			: vignetteModalLabels?.labelCancelSale || 'Entfernen und zum Warenkorb';
	};

	const renderCartOverlappItems = () => {
		if (!cartOverlapItems || !cartOverlapItems.length) return null;

		return (
			<div>
				<Typography className="px-3 mb-3" component="p" color="title" size="lg">
					{vignetteModalLabels?.multipleCartDuplicateTitle || 'Doppelte Produkte'}
				</Typography>
				{selectedProducts
					.filter((prod: IVignetteOffer) =>
						cartOverlapItems.filter((el) => el.variationCode === prod.variationCode),
					)
					.map((offer, index) => (
						<ProductSummary
							key={offer.serviceCatalogCode}
							multipleProducts
							className="mb-3 last:mb-0"
							style={{ borderColor: '#EBEBEB' }}
							title={offer.serviceCatalogName}
							offer={offer}
							openProductKeyIndex={index}
							cardIsOpen={openProductKey === `${offer.serviceCatalogId}-${index}`}
							icon={renderProductIcon({
								moduleTypeId: offer.moduleTypeId,
								supplierId: offer.supplierId,
								tollClass: offer.tollClass,
							})}
							handleToggle={handleToggle}>
							{offer.dates ? (
								<ProductSummaryElement
									label="Gültig"
									value={`${formatDate(offer.dates.start)} - ${formatDate(offer.dates.end)}`}
								/>
							) : null}
							<ProductSummaryElement label="Kennzeichen" value={formatPlates(offer.plates)} />
						</ProductSummary>
					))}
			</div>
		);
	};

	return (
		<Modal
			withSubmissionButtons
			title={getValidationTitle()}
			isOpen={isOpen}
			submitButtonLabel={getSubmitButtonLabel()}
			cancelButtonLabel={getCancelButtonLabel()}
			onSubmit={onSubmit}
			onCancel={onCancel}
			onClose={onClose}>
			<div className="flex flex-col gap-3">
				<Typography component="p" color="title" className="px-3" size="lg">
					{getValidationTextMessage()}
				</Typography>
				{renderCartOverlappItems()}
			</div>
		</Modal>
	);
};
